import { Badge, Banner, Button } from "@shopify/polaris";
import { ClockMajor } from "@shopify/polaris-icons";
import { getDaysBetweenDates } from "@smartrr/shared/utils/dateUtils";
import { pluralize } from "@smartrr/shared/utils/pluralize";
import { DateTime } from "luxon";
import React from "react";
import styled from "styled-components";

import { useSmartrrVendorSelector } from "@vendor-app/app/_state/typedVendorReduxHooks";

const FREE_TRIAL_PLAN_NAME = "Free Trial";

const BannerWrapper = styled.div`
  margin-bottom: 1rem;

  .Polaris-Badge {
    position: absolute;
    right: 20px;
    top: 20px;
  }
`;

function getDaySuffix(day: number): string {
  if ([11, 12, 13].includes(day)) {
    return "th";
  }

  const lastDigit = day % 10;
  return { 1: "st", 2: "nd", 3: "rd" }[lastDigit] ?? "th";
}

/**
 * Format the date as "January 30th"
 */
function formatTrialEndDate(isoDate: string): string {
  const date = DateTime.fromISO(isoDate);
  if (!date.isValid) {
    return "";
  }

  return `${date.toFormat("MMMM")} ${date.day}${getDaySuffix(date.day)}`;
}

interface TrialBannerProps {
  // The Account page already renders its own banner for the legacy Free Trial plan
  includeLegacyFreeTrial?: boolean;
}

export function TrialBanner({ includeLegacyFreeTrial = true }: TrialBannerProps): React.JSX.Element | null {
  const activePlan = useSmartrrVendorSelector(state => state.accountPlans.activePlan);

  if (!activePlan) {
    return null;
  }

  const isFreeTrial = activePlan.planName === FREE_TRIAL_PLAN_NAME;
  const hasTrialEnd = !!activePlan.trialPeriodEnd;
  const isPaidPlanTrial =
    !isFreeTrial && hasTrialEnd && DateTime.fromISO(activePlan.trialPeriodEnd!) > DateTime.now();

  if (isFreeTrial && !includeLegacyFreeTrial) {
    return null;
  }

  if (!isFreeTrial && !isPaidPlanTrial) {
    return null;
  }

  const trialDaysLeft = hasTrialEnd ? getDaysBetweenDates(new Date(), new Date(activePlan.trialPeriodEnd!)) : 0;
  const remaining = `${pluralize(trialDaysLeft, "day")} remaining`;
  const trialEndDate = hasTrialEnd ? formatTrialEndDate(activePlan.trialPeriodEnd!) : "";

  if (isFreeTrial) {
    return (
      <BannerWrapper>
        <Banner title="Free trial" icon={ClockMajor} status="info">
          <Badge status="info">{remaining}</Badge>
          <p>
            <Button plain url="/admin/smartrr-account">
              Select a plan
            </Button>
            &nbsp;by {trialEndDate} in order to keep access to all your existing features.
          </p>
        </Banner>
      </BannerWrapper>
    );
  }

  return (
    <BannerWrapper>
      <Banner title="Free trial" icon={ClockMajor} status="info">
        <Badge status="info">{remaining}</Badge>
        <p>
          {`Your free trial of the ${activePlan.planName} plan ends on ${trialEndDate}. Billing begins after that date.`}
        </p>
      </Banner>
    </BannerWrapper>
  );
}
